import { ArrowRight, GraduationCap } from "lucide-react";
import { Link } from "react-router-dom";

const ICON_STROKE = 1.5;

/** Real counts derived from the programs catalog — no invented numbers. */
function computeProgramStats(programsOffered) {
  const columns = programsOffered?.columns ?? [];
  let schools = 0;
  let bachelors = 0;
  let masters = 0;
  for (const col of columns) {
    for (const school of col.schools ?? []) {
      schools += 1;
      bachelors += school.degrees?.length ?? 0;
    }
    masters += col.masters?.degrees?.length ?? 0;
  }
  return { schools, bachelors, masters };
}

export function Hero({ uni, heroImageSrc }) {
  const { schools, bachelors, masters } = computeProgramStats(uni.programsOffered);
  const stats = [
    { value: `${schools}`, label: "Schools" },
    { value: `${bachelors}`, label: "Bachelor's programs" },
    { value: `${masters}`, label: "Master's programs" },
  ].filter((s) => s.value !== "0");

  return (
    <section
      className="relative isolate w-full min-h-[min(85vh,52rem)] overflow-hidden bg-ink-900"
      aria-labelledby="hero-title"
    >
      <div className="absolute inset-0">
        <img
          src={heroImageSrc}
          alt=""
          className="h-full w-full object-cover object-[center_35%] opacity-90 sm:object-[center_30%]"
          width={1600}
          height={900}
          decoding="async"
        />
        <div
          className="absolute inset-0 bg-gradient-to-r from-ink-900 via-ink-900/85 to-maroon-900/55 sm:via-ink-900/75"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -right-24 top-0 h-96 w-96 rounded-full bg-maroon-600/25 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-ink-900 to-transparent"
          aria-hidden
        />
      </div>

      <div className="relative mx-auto flex min-h-[min(85vh,52rem)] max-w-6xl flex-col justify-center px-4 py-14 sm:px-6 sm:py-20 lg:py-24">
        <div className="max-w-xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-maroon-100 backdrop-blur-sm">
            <GraduationCap className="h-3.5 w-3.5" strokeWidth={ICON_STROKE} aria-hidden />
            Admissions open
          </span>
          <h1
            id="hero-title"
            className="mt-5 font-bold tracking-tight text-white drop-shadow-sm"
          >
            <span className="block text-3xl leading-tight sm:text-4xl lg:text-5xl">
              Welcome to
            </span>
            <span className="mt-2 block bg-gradient-to-r from-white via-maroon-100 to-maroon-200 bg-clip-text text-3xl text-transparent sm:text-4xl lg:text-[2.75rem] lg:leading-tight">
              {uni.shortName}
            </span>
          </h1>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-200 sm:text-lg">
            {uni.tagline} — shaping ethical graduates through accredited programs,
            mentorship, and community-centered learning across Somaliland and beyond.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Link
              to="/apply"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-maroon-700 px-6 text-sm font-semibold text-white shadow-lg shadow-maroon-900/40 transition hover:bg-maroon-600"
            >
              Apply now
              <ArrowRight className="h-5 w-5" strokeWidth={ICON_STROKE} aria-hidden />
            </Link>
            <Link
              to="/#programs"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-white/25 bg-white/10 px-6 text-sm font-semibold text-white backdrop-blur-sm transition hover:bg-white/15"
            >
              Explore programs
            </Link>
            <Link
              to="/student-results"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-white/25 bg-white/10 px-6 text-sm font-semibold text-white backdrop-blur-sm transition hover:bg-white/15"
            >
              <GraduationCap className="h-5 w-5" strokeWidth={ICON_STROKE} aria-hidden />
              Student results
            </Link>
          </div>

          {stats.length > 0 ? (
            <dl className="mt-12 grid grid-cols-3 gap-4 border-t border-white/10 pt-6 sm:max-w-md">
              {stats.map((s) => (
                <div key={s.label}>
                  <dt className="sr-only">{s.label}</dt>
                  <dd className="text-2xl font-bold text-white sm:text-3xl">{s.value}</dd>
                  <p className="mt-1 text-xs leading-snug text-slate-300 sm:text-sm">{s.label}</p>
                </div>
              ))}
            </dl>
          ) : null}
        </div>
      </div>
    </section>
  );
}
